import React, { useState, useCallback } from 'react';
import styles from '../ReportCard.module.scss';
import moment from 'moment';
import MiniMap from '../MiniMap';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import { formatDuration } from '../utils/formatDuration';
import { getDriverDocuments } from 'service/DriverService';

/**
 * Таблица загрузок со статусом "В погрузке".
 */
const InLoadingTableView = ({
  inLoadingLoads,
  inLoadingCount,
  inLoadingNext,
  inLoadingPrev,
  inLoadingPage,
  setInLoadingPage,
  inLoadingSearch,
  setInLoadingSearch,
  nowTime,
  user,
}) => {
  const [visibleMaps, setVisibleMaps] = useState({});
  const [showLegend, setShowLegend] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMaps, setDetailMaps] = useState({});
  const [driverDocs, setDriverDocs] = useState({});
  const [docPreview, setDocPreview] = useState(null);

  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDetailMap = (mapKey) => {
    setDetailMaps(prev => ({ ...prev, [mapKey]: !prev[mapKey] }));
  };

  const fetchDocumentsIfNeeded = useCallback(async (item) => {
    if (!user?.is_admin_of && !user?.is_report_of) return;
    const driverId = item.driver_id || item.driver;
    if (!driverId) return;
    const itemId = item.id;
    if (driverDocs[itemId]) return;
    setDriverDocs(prev => ({ ...prev, [itemId]: { loading: true, error: null, docs: [] } }));
    try {
      const res = await getDriverDocuments(driverId);
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: null, docs: res.data } }));
    } catch (e) {
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: 'Ошибка загрузки', docs: [] } }));
    }
  }, [user, driverDocs]);

  const handleRowClick = (item) => {
    const id = item.id;
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
      setDetailMaps({});
      fetchDocumentsIfNeeded(item);
    }
  };

  const renderDetailCard = (item, duration) => {
    const baseName = item.top_base_name || item.chpp_name || item.private_point_name || '—';
    const canSeeDocs = user?.is_admin_of || user?.is_report_of;
    const docsState = driverDocs[item.id];

    const GeoField = ({ geo, mapKey }) => {
      if (!geo) return null;
      return (
        <div>
          <div className={styles.detailGeoRow}>
            <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${geo}`, '_blank')}>📍 Открыть на карте</button>
            <button className={styles.detailGeoBtn} onClick={() => toggleDetailMap(mapKey)}>{detailMaps[mapKey] ? '✕ Скрыть карту' : '🗺️ Мини-карта'}</button>
          </div>
          {detailMaps[mapKey] && <div className={styles.detailMapWrap}><MiniMap geo={geo} /></div>}
        </div>
      );
    };

    const InfoItem = ({ label, value }) => (
      <div className={styles.detailInfoItem}>
        <span className={styles.detailInfoLabel}>{label}</span>
        <div className={styles.detailInfoValue}>{value}</div>
      </div>
    );

    const PhoneLink = ({ phone }) => {
      if (!phone || phone === '-') return <span>—</span>;
      return <a href={`tel:${phone}`} className={styles.detailPhoneLink}>📞 {phone}</a>;
    };

    return (
      <div className={styles.detailCard}>
        {/* Шапка */}
        <div className={styles.detailCardTopBar}>
          <div className={styles.detailCardTitle}>
            <span>⛏️</span> Подробности погрузки
          </div>
          <div className={styles.detailDurationBadges}>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeLoading}`}>
              <span>⏱️ Идёт погрузка</span>
              <span>{duration || '—'}</span>
            </div>
          </div>
        </div>

        <div className={styles.detailBody}>
          {item.notes && (
            <div className={styles.detailNotes}>
              <strong>Примечания:</strong> {item.notes}
            </div>
          )}
          {/* Водитель */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>👤 Водитель</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="ФИО" value={item.employee_name || '—'} />
              <InfoItem label="Телефон" value={<PhoneLink phone={item.employee_phone} />} />
              <InfoItem label="Гос. номер" value={item.vehicle_number || '—'} />
              <InfoItem label="Модель" value={item.vehicle_model || '—'} />
            </div>
            {canSeeDocs && (() => {
              const DOC_LABELS = ['Фото ТС','Паспорт (л)','Паспорт (о)','Права (л)','Права (о)','Тех.пасп. (л)','Тех.пасп. (о)'];
              const byDesc = {};
              if (docsState?.docs) docsState.docs.forEach(d => { if (d.description) byDesc[d.description] = d; });
              const otherDocs = docsState?.docs?.filter(d => !DOC_LABELS.includes(d.description)) || [];
              return (
                <div className={styles.detailDocsWrap}>
                  <div className={styles.detailDocsTitle}>📎 Документы водителя</div>
                  {!docsState || docsState.loading ? (
                    <div className={styles.detailDocsLoading}>Загрузка документов…</div>
                  ) : docsState.error ? (
                    <div className={styles.detailDocsError}>{docsState.error}</div>
                  ) : (
                    <>
                      <div className={styles.detailDocsGrid}>
                        {DOC_LABELS.map(label => {
                          const doc = byDesc[label];
                          const fileUrl = doc ? (doc.file_url || doc.file) : null;
                          const isActive = docPreview?.url === fileUrl && fileUrl;
                          return (
                            <button key={label}
                              className={`${styles.detailDocBtn} ${doc ? styles.detailDocBtnHas : styles.detailDocBtnMissing} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => { if (!fileUrl) return; setDocPreview(isActive ? null : { url: fileUrl, label }); }}
                              title={doc ? `Открыть: ${label}` : `${label} — не загружено`}
                            >
                              <span className={styles.detailDocBtnIcon}>{doc ? '' : '—'}</span>
                              <span className={styles.detailDocBtnLabel}>{label}</span>
                            </button>
                          );
                        })}
                        {otherDocs.map(doc => {
                          const fileUrl = doc.file_url || doc.file;
                          const isActive = docPreview?.url === fileUrl;
                          return (
                            <button key={doc.id}
                              className={`${styles.detailDocBtn} ${styles.detailDocBtnHas} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.description || doc.file_name || 'Файл' })}
                            >
                              <span className={styles.detailDocBtnIcon}>📁</span>
                              <span className={styles.detailDocBtnLabel}>{doc.description || doc.file_name || 'Файл'}</span>
                            </button>
                          );
                        })}
                        {!docsState?.docs?.length && (
                          <div className={styles.detailDocsEmpty} style={{ gridColumn: '1 / -1' }}>Документы не загружены</div>
                        )}
                      </div>
                      {docPreview && (
                        <div className={styles.detailDocPreview}>
                          <div className={styles.detailDocPreviewHeader}>
                            <div className={styles.detailDocPreviewTitle}>
                              <span className={styles.detailDocPreviewIcon}>📄</span>
                              <span>{docPreview.label}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>↗️ Открыть</a>
                              <button className={styles.detailDocPreviewClose} onClick={() => setDocPreview(null)}>✕</button>
                            </div>
                          </div>
                          <div className={styles.detailDocPreviewBody}>
                            <img src={docPreview.url} alt={docPreview.label} className={styles.detailDocPreviewImg}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>⇩ Скачать файл</a>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* База назначения */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🏭 База назначения</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Название" value={<span style={{ color: '#0d6efd', fontWeight: 700 }}>{baseName}</span>} />
              <InfoItem label="Телефон" value={<PhoneLink phone={item.top_base_data?.phone} />} />
              {(item.top_base_name ? 'Топливная база' : item.chpp_name ? 'ТЭЦ' : item.private_point_name ? 'Соц. объект / МСУ' : null) && (
                <InfoItem label="Тип" value={item.top_base_name ? 'Топливная база' : item.chpp_name ? 'ТЭЦ' : 'Соц. объект / МСУ'} />
              )}
            </div>
            {Array.isArray(item.geo_base) && item.geo_base.length > 0 && (
              <div className={styles.detailSubSection}>
                <div className={styles.detailSubSectionTitle}>📍 Точки базы</div>
                {item.geo_base.map((g, i) => (
                  <div key={g.id ?? i} className={styles.detailBranchRow}>
                    <span className={styles.detailBranchName}>{g.name}</span>
                    {g.address && <span className={styles.detailBranchAddr}>{g.address}</span>}
                    {g.geo && (
                      <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${g.geo}`, '_blank')}>📍 Открыть</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Въезд */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🚛 Въезд в Кара-Кече</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Время въезда" value={item.time_a || '—'} />
              <InfoItem label="СБ въезда" value={item.guard_name || '—'} />
              <InfoItem label="Телефон СБ" value={<PhoneLink phone={item.guard_phone} />} />
            </div>
            {item.geo_a && (
              <div className={styles.detailGeoSection}>
                <span className={styles.detailGeoLabel}>Геолокация въезда</span>
                <GeoField geo={item.geo_a} mapKey={`detail-${item.id}-geo_a`} />
              </div>
            )}
            {item.photo_a && (
              <div className={styles.detailPhotoConfirm}>
                <div className={styles.detailPhotoConfirmLabel}>📷 Подтверждение въезда</div>
                <a href={item.photo_a} target="_blank" rel="noopener noreferrer">
                  <img src={item.photo_a} alt="Фото въезда" className={styles.detailPhoto} />
                </a>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className={styles.table_info_wrapper}>
      {showLegend && (
        <div className={styles.legendOverlay} onClick={() => setShowLegend(false)}>
          <div className={styles.legendModal} onClick={e => e.stopPropagation()}>
            <div className={styles.legendHeader}>
              <span>🎨 Значение цветов строк</span>
              <button className={styles.legendClose} onClick={() => setShowLegend(false)}>✕</button>
            </div>
            <div className={styles.legendBody}>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#fff', border: '1px solid #dee2e6' }} />
                <div>
                  <strong>Обычный фон</strong>
                  <div>Время в погрузке менее 1 суток или указано примечание</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#FFC107' }} />
                <div>
                  <strong>Жёлтый</strong>
                  <div>Время в погрузке более 1 суток (без примечания)</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: '#f8d7da' }} />
                <div>
                  <strong>Красноватый</strong>
                  <div>Время в погрузке более 2 суток (без примечания)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Pagination
        page={inLoadingPage}
        setPage={setInLoadingPage}
        count={inLoadingCount}
        hasNext={!!inLoadingNext}
        hasPrev={!!inLoadingPrev}
        shownCount={inLoadingLoads.length}
        onLegend={() => setShowLegend(true)}
      />

      <SearchBar
        value={inLoadingSearch}
        onChange={setInLoadingSearch}
        onClear={() => setInLoadingSearch('')}
        placeholder="Поиск по водителю, телефону, Базе, номеру машины, модели машины..."
      />

      <div className={styles.tableScrollWrap}>
        <table className={`${styles.dataTable} ${styles.inLoadingTable}`}>
          <thead>
            <tr>
              <th>#</th>
              <th>Машина</th>
              <th>Водитель</th>
              <th>База назначения</th>
              <th>Въезд (Кара-Кече)</th>
              <th>Геолокация</th>
              <th>СБ въезда</th>
              <th>Длительность</th>
              <th>Примечания</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(inLoadingLoads) ? inLoadingLoads : []).map((item, index) => {
              const sequentialNumber = (inLoadingPage - 1) * 10 + index + 1;
              let timeA = null;
              if (item.time_a) {
                timeA = moment(item.time_a, 'DD.MM.YYYY HH:mm', true);
                if (!timeA.isValid()) {
                  timeA = moment(item.time_a);
                }
              }
              const duration = timeA && timeA.isValid() ? formatDuration(timeA, nowTime) : '';
              const mapKey = `${item.id}-geo_a`;

              const hasNotes = item.notes && item.notes.trim();
              let rowBg = {};
              if (!hasNotes && timeA && timeA.isValid()) {
                const hoursInLoading = nowTime.diff(timeA, 'hours');
                if (hoursInLoading >= 48) {
                  rowBg = { background: '#f8d7da' };
                } else if (hoursInLoading >= 24) {
                  rowBg = { background: '#FFC107' };
                }
              }

              return (
                <React.Fragment key={item.id}>
                  <tr
                    onDoubleClick={() => handleRowClick(item)}
                    onClick={() => { if (window.innerWidth <= 640) handleRowClick(item); }}
                    style={{ ...rowBg, cursor: 'pointer', userSelect: 'none', background: expandedRowId === item.id ? '#eef4ff' : rowBg.background }}
                    title="Двойной клик — подробная карточка"
                  >
                    <td>{sequentialNumber}</td>
                    <td data-label="Машина">
                      <div style={{ fontWeight: '700' }}>{item.vehicle_number || '-'}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{item.vehicle_model || '-'}</div>
                    </td>
                    <td data-label="Водитель">
                      <div>👤 {item.employee_name || 'Не указан'}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{item.employee_phone || '-'}</div>
                    </td>
                    <td data-label="База" style={{ color: '#0d6efd', fontWeight: '600' }}>
                      {item.top_base_name || item.chpp_name || item.private_point_name || '-'}
                    </td>
                    <td data-label="Въезд">{item.time_a || '-'}</td>
                    <td data-label="Гео">
                      {item.geo_a ? (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button
                            onClick={e => { e.stopPropagation(); window.open(`https://www.google.com/maps?q=${item.geo_a}`, '_blank'); }}
                            className={styles.geoLinkBtn}
                            title="Открыть на карте"
                          >📍</button>
                          <button
                            onClick={e => { e.stopPropagation(); toggleMap(item.id, 'geo_a'); }}
                            className={styles.mapToggleBtn}
                            title="Мини-карта"
                          >{visibleMaps[mapKey] ? '✕' : '🗺️'}</button>
                        </div>
                      ) : '-'}
                    </td>
                    <td data-label="СБ">
                      <div>{item.guard_name || '-'}</div>
                      {item.guard_phone && (
                        <div style={{ fontSize: '12px', color: '#888' }}>📞 {item.guard_phone}</div>
                      )}
                    </td>
                    <td data-label="Длительность">{duration || '-'}</td>
                    <td data-label="Примечания" style={{ fontSize: '12px' }}>{item.notes || '-'}</td>
                  </tr>
                  {item.geo_a && visibleMaps[mapKey] && (
                    <tr>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                          <MiniMap geo={item.geo_a} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {expandedRowId === item.id && (
                    <tr className={styles.detailCardRow}>
                      <td colSpan={9}>
                        {renderDetailCard(item, duration)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={inLoadingPage}
        setPage={setInLoadingPage}
        count={inLoadingCount}
        hasNext={!!inLoadingNext}
        hasPrev={!!inLoadingPrev}
        shownCount={inLoadingLoads.length}
        style={{ marginTop: '8px', marginBottom: 0 }}
      />
    </div>
  );
};

export default React.memo(InLoadingTableView);

